import { CARET_PLACEMENTS, Text, Tooltip, Message, MessageInline, DesignLanguage, useIsDesignLanguage } from '@afterpaytouch/core'
import React, { FunctionComponent, ReactNode, useContext, useRef } from 'react'
import { ModuleContainer } from '..'
import { ShippingOptionErrorType } from '../../model'
import { useShippingOption } from '../../state'

import { useTranslation } from '../../utils/convergence'
import { useCheckoutSizes } from '../../utils/responsive'
import { ShippingOptionContext } from './context'

export const ShippingOptionWarningMessage: FunctionComponent = () => {
  const { t } = useTranslation()
  const { error } = useShippingOption()
  const isShippingOptionUnableToUpdate = error === ShippingOptionErrorType.ShippingOptionUnableToUpdate
  const { setShowWarningMessage, showWarningMessage } = useContext(ShippingOptionContext)
  const { smallTextSize } = useCheckoutSizes()
  const tooltipRef = useRef(null)
  const isArcadeDesignLanguage = useIsDesignLanguage(DesignLanguage.Arcade)

  const tooltipContent = (): ReactNode => {
    if (isShippingOptionUnableToUpdate) {
      return <Text size={smallTextSize}>{t('shippingOption:unableToSelectOption')}</Text>
    }
    return <Text size={smallTextSize}>{t('shippingOption:someShippingOptionsAreNotAvailableWarning')}</Text>
  }

  const closeTooltip = (): void => {
    setShowWarningMessage(false)
  }

  if (!showWarningMessage) {
    return null
  }

  if (isArcadeDesignLanguage) {
    if (isShippingOptionUnableToUpdate) {
      return (
        <MessageInline
          kind='Error'
          description={<Text size={smallTextSize}>{t('shippingOption:unableToSelectOptionMessage.description_inline')}</Text>}
          testNameSpace='shipping-options-warning-message'
        />
      )
    }
    return (
      <MessageInline
        kind='Info'
        description={<Text size={smallTextSize}>{t('shippingOption:someShippingOptionsAreNotAvailableWarningMessage.description_inline')}</Text>}
        testNameSpace='shipping-options-warning-message'
      />
    )
  }

  if (isArcadeDesignLanguage) {
    if (isShippingOptionUnableToUpdate) {
      return (
        <ModuleContainer padding={{ vertical: 'Small' }} showBorder={false}>
          <Message
            testNameSpace='shipping-options-warning'
            kind='Error'
            heading={t('shippingOption:unableToSelectOptionMessage.heading')}
            description={t('shippingOption:unableToSelectOptionMessage.description')}
          />
        </ModuleContainer>
      )
    }
    return (
      <ModuleContainer padding={{ vertical: 'Small' }} showBorder={false}>
        <Message
          testNameSpace='shipping-options-warning'
          kind='Basic'
          heading={t('shippingOption:someShippingOptionsAreNotAvailableWarningMessage.heading')}
          description={t('shippingOption:someShippingOptionsAreNotAvailableWarningMessage.description')}
          iconName='Info'
        />
      </ModuleContainer>
    )
  }

  return (
    <ModuleContainer nested extraTopMargin>
      <Tooltip
        id='shipping-option-not-all-warn-tooltip'
        testNameSpace='shipping-options-warning-tooltip'
        content={tooltipContent()}
        caretPlacement={CARET_PLACEMENTS.BOTTOM}
        triggerRef={tooltipRef}
        usePortal={false}
        inline
        onClose={closeTooltip}
      />
    </ModuleContainer>
  )
}
